import React from 'react'
import { useActions, useMountedLogic, useValues } from 'kea'
import Modal from 'react-responsive-modal'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import I18n from 'lib/i18n'
import Button from 'lib/tags/button'
import Select from 'lib/forms/select'
import Spinner from 'lib/tags/spinner'

import countryMenuForm, { Field } from './form'
import menuLogic from '../logic'
import countryMenuSaga from './saga'

const CountriesMenu = () => {
  const { openCountryModal, loadCountries, closeCountryModal } = useActions(menuLogic)
  const {
    isCountryModalOpen,
    countryMenuOptions: { countries, languages, currencies },
    countryMenuInformation
  } = useValues(menuLogic)
  const { setValues, submit } = useActions(countryMenuForm)
  const { isRedirecting, hasChanges } = useValues(countryMenuForm)
  useMountedLogic(countryMenuSaga)

  const savedCountry = countryMenuInformation.countryName
  const savedLanguage = languages.filter(language => language.value === countryMenuInformation.language)[0]
  const savedLanguageLabel = savedLanguage ? savedLanguage.label : ''
  const savedLanguageCode = savedLanguage ? savedLanguage.shortcode.toUpperCase() : ''
  const longCountry = savedCountry.split(/[- ]/).filter(word => word.length > 21 - savedLanguageLabel.length).length > 0

  const alignItemStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center' }

  const handleCancel = () => {
    setValues(countryMenuInformation)
    closeCountryModal()
  }

  const handleOpen = () => {
    const language = languages.filter(language => language.value === countryMenuInformation.language)[0]

    if (language) {
      loadCountries(language.shortcode)
    }

    openCountryModal()
  }

  return (
    <li className='country-menu' data-attr='country-menu'>
      <div className='country-menu-item' data-attr='country-menu-item' onClick={handleOpen}>
        <span className='with-flag' style={alignItemStyle}>
          <LazyLoadComponent>
            <div
              className='country-flag'
              style={{
                backgroundImage: `url(/assets/design1/icons/round-flags/${countryMenuInformation.country.toLowerCase()}.svg)`
              }}
            />
          </LazyLoadComponent>
        </span>
        <span className='country-option' style={alignItemStyle} data-attr='country'>
          {savedCountry}
        </span>
        <span className='separator' />
        <span data-attr='language' style={alignItemStyle}>
          {longCountry ? savedLanguageCode : savedLanguageLabel}
        </span>
        <span className='separator' />
        <span data-attr='currency' style={alignItemStyle}>
          {countryMenuInformation.currency}
        </span>
      </div>
      <Modal
        styles={{
          modal: {
            padding: '0px',
            borderRadius: '3px'
          },
          overlay: {
            zIndex: 9999
          }
        }}
        center
        open={isCountryModalOpen}
        onClose={handleCancel}
        showCloseIcon={false}
      >
        <div className='country-menu-modal' data-attr='country-menu-modal-container'>
          <h5 data-attr='title'>{I18n.t('layouts.header.menu.country_menu.modal_title')}</h5>
          {countries && countries.length ? (
            <div className='content-bloc'>
              <Field
                name='country'
                dataAttr='country-field'
                label={I18n.t('layouts.header.menu.country_menu.country')}
                size='medium'
              >
                {({ value, setValue }) => (
                  <div>
                    <Select value={value} options={countries} onValueChange={setValue} withOrder />
                  </div>
                )}
              </Field>
              <Field
                name='language'
                dataAttr='language-field'
                label={I18n.t('layouts.header.menu.country_menu.language')}
                size='medium'
              >
                {({ value, setValue }) => (
                  <div>
                    <Select value={value} options={languages} onValueChange={l => setValue(parseInt(l))} />
                  </div>
                )}
              </Field>
              <Field
                name='currency'
                dataAttr='currency-field'
                label={I18n.t('layouts.header.menu.country_menu.currency')}
                size='medium'
              >
                {({ value, setValue }) => (
                  <div>
                    <Select value={value} options={currencies} onValueChange={setValue} />
                  </div>
                )}
              </Field>
            </div>
          ) : (
            <div className='content-bloc'>
              <Spinner color='gray' />
            </div>
          )}

          <div className='country-menu-modal-actions'>
            {isRedirecting && (
              <div className='spinners'>
                <Spinner color='gray' />
              </div>
            )}
            <div className='modal-buttons'>
              <Button
                color='white'
                backgroundColor='green'
                className='submit-btn'
                dataAttr='cancel-button'
                onClick={handleCancel}
                isSubmitting={false}
                disabled={isRedirecting}
                isStyleDisabled
                content={I18n.t('layouts.header.menu.country_menu.cancel')}
              />
              <Button
                color='white'
                backgroundColor='green'
                className='submit-btn'
                dataAttr='save-button'
                onClick={hasChanges ? submit : closeCountryModal}
                isSubmitting={false}
                disabled={isRedirecting}
                isStyleDisabled={false}
                content={I18n.t('layouts.header.menu.country_menu.save')}
              />
            </div>
          </div>
        </div>
      </Modal>
    </li>
  )
}

export default CountriesMenu
